import cn from 'classnames';
import { SelectField } from 'fields/SelectField';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Response } from 'api/apiBase/Response';
import { IPairWallet } from 'api/apiPairs/models';
import { bnFrom } from 'tools/math';
import { EDexPairFilters } from 'types/filters';
import { IDexPair } from 'types/pairs';
import { FiltersPopup } from 'ui/Filters';
import { TableAddressChip } from 'ui/Table/TableAddressChip/TableAddressChip';
import { TableCheckbox } from 'ui/Table/TableCheckbox/TableCheckbox';
import { formatToken } from 'utils/formats/formatToken';
import { EExchange } from 'web3';

import { SourceWalletSummary } from './SourceWalletSummary';

import { useTransferWallets } from '../../api/useTransferWallets';
import { buildRecipientsFromWallets, getTransferTokenOptions } from '../../lib/tokens';
import { useTransferWizardStore } from '../../model/transferWizard.store';

interface StepSelectRecipientsProps {
  pair: IDexPair;
  sourceWallet: IPairWallet;
  onReadyChange: (isReady: boolean) => void;
}

type TransferWalletsPage = Response<{ has_next: boolean; items: IPairWallet[] }>;

export interface StepSelectRecipientsHandle {
  triggerBalanceWarning: () => void;
}

export const StepSelectRecipients = forwardRef<
  StepSelectRecipientsHandle,
  StepSelectRecipientsProps
>(({ pair, sourceWallet, onReadyChange }, ref) => {
  const token = useTransferWizardStore(state => state.token);
  const setToken = useTransferWizardStore(state => state.setToken);
  const setRecipients = useTransferWizardStore(state => state.setRecipients);
  const recipients = useTransferWizardStore(state => state.recipients);

  const tokenOptions = useMemo(() => getTransferTokenOptions(pair), [pair]);

  useEffect(() => {
    if (!token && tokenOptions.length) {
      setToken(tokenOptions[0]);
    }
  }, [token, tokenOptions, setToken]);

  const sourceTokenBalance = useMemo(() => {
    if (!token) return bnFrom(0);

    const balance =
      sourceWallet.tokens?.find(walletToken => walletToken.token.id === token.id)?.balance ?? '0';

    return bnFrom(balance);
  }, [sourceWallet, token]);

  const hasSourceTokenBalance = useMemo(
    () => Boolean(token) && !sourceTokenBalance.isZero(),
    [token, sourceTokenBalance],
  );

  const balanceShakeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const balanceElementRef = useRef<HTMLDivElement>(null);
  const [shouldShake, setShouldShake] = useState(false);

  const triggerBalanceWarning = useCallback(() => {
    if (balanceShakeTimeoutRef.current) {
      clearTimeout(balanceShakeTimeoutRef.current);
      balanceShakeTimeoutRef.current = null;
    }

    // Force animation restart by removing and re-adding the class
    setShouldShake(false);

    requestAnimationFrame(() => {
      setShouldShake(true);

      // Reset after animation completes
      balanceShakeTimeoutRef.current = setTimeout(() => {
        setShouldShake(false);
        balanceShakeTimeoutRef.current = null;
      }, 450);
    });
  }, []);

  useImperativeHandle(ref, () => ({
    triggerBalanceWarning,
  }));

  useEffect(() => {
    if (!token || hasSourceTokenBalance) {
      setShouldShake(false);

      if (balanceShakeTimeoutRef.current) {
        clearTimeout(balanceShakeTimeoutRef.current);
        balanceShakeTimeoutRef.current = null;
      }

      return;
    }

    triggerBalanceWarning();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token?.id, hasSourceTokenBalance, triggerBalanceWarning]);

  useEffect(() => {
    return () => {
      if (balanceShakeTimeoutRef.current) {
        clearTimeout(balanceShakeTimeoutRef.current);
      }
    };
  }, []);

  const transferWalletsQuery = useTransferWallets(pair, EDexPairFilters.walletsTransfer);
  const { fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } = transferWalletsQuery;

  const wallets = useMemo(() => {
    const pages = ((transferWalletsQuery.data as any)?.pages ?? []) as TransferWalletsPage[];
    const items = pages.flatMap(page => page.data?.items ?? []);

    return items.filter((wallet: IPairWallet) => wallet.id !== sourceWallet.id);
  }, [transferWalletsQuery.data, sourceWallet.id]);

  const [selectedIds, setSelectedIds] = useState<number[]>(() => recipients.map(r => r.walletId));

  useEffect(() => {
    const nextIds = recipients.map(r => r.walletId);
    setSelectedIds(prev => {
      if (prev.length === nextIds.length && prev.every((id, index) => id === nextIds[index])) {
        return prev;
      }
      return nextIds;
    });
  }, [recipients]);

  useEffect(() => {
    setSelectedIds(prev => prev.filter(id => wallets.some(wallet => wallet.id === id)));
  }, [wallets]);

  useEffect(() => {
    if (!token) return;
    const selectedWallets = wallets.filter(wallet => selectedIds.includes(wallet.id));
    const nextRecipients = buildRecipientsFromWallets({
      wallets: selectedWallets,
      token,
      previous: recipients,
    });

    const hasChanged =
      nextRecipients.length !== recipients.length ||
      nextRecipients.some(nextRecipient => {
        const existing = recipients.find(r => r.walletId === nextRecipient.walletId);
        if (!existing) return true;
        if (!existing.balanceWei.eq(nextRecipient.balanceWei)) return true;
        return false;
      });

    if (hasChanged) {
      setRecipients(nextRecipients);
    }
  }, [token, wallets, selectedIds, recipients, setRecipients]);

  useEffect(() => {
    onReadyChange(Boolean(token) && selectedIds.length > 0 && hasSourceTokenBalance);
  }, [selectedIds.length, token, hasSourceTokenBalance, onReadyChange]);

  const handleToggle = useCallback((wallet: IPairWallet) => {
    setSelectedIds(prev => {
      const exists = prev.includes(wallet.id);
      const next = exists ? prev.filter(id => id !== wallet.id) : prev.concat(wallet.id);
      return next;
    });
  }, []);

  const allWalletIds = useMemo(() => wallets.map(wallet => wallet.id), [wallets]);
  const allSelected = selectedIds.length > 0 && selectedIds.length === allWalletIds.length;
  const isIndeterminate = selectedIds.length > 0 && selectedIds.length < allWalletIds.length;

  const handleToggleAll = useCallback(() => {
    setSelectedIds(prev => (prev.length === allWalletIds.length ? [] : [...allWalletIds]));
  }, [allWalletIds]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.target as HTMLDivElement;
      if (!hasNextPage || isFetchingNextPage) return;
      if (target.scrollHeight - target.scrollTop - target.clientHeight < 160) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  if (!token) {
    return null;
  }

  const tokenBalanceDisplay = (
    <div
      ref={balanceElementRef}
      className={cn('inline-block', {
        'animate-shake': shouldShake,
      })}
    >
      <span
        className={cn('text-xs transition-colors', {
          'text-gray-400': hasSourceTokenBalance,
          'text-error-1': !hasSourceTokenBalance,
        })}
      >
        Balance: {formatToken(sourceTokenBalance, token.decimals)} {token.symbol}
      </span>
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 md:grid-cols-2">
        <SourceWalletSummary
          pair={pair}
          sourceWallet={sourceWallet}
          balance={tokenBalanceDisplay}
        />
        <div className="flex flex-col gap-2">
          <label className="text-sm text-gray-300" htmlFor="transfer-token-select">
            Token
          </label>
          <SelectField
            id="transfer-token-select"
            className="w-full"
            items={tokenOptions}
            itemToString={option => option.symbol}
            selectedItem={token}
            onSelectItem={option => {
              if (option) {
                setToken(option);
              }
            }}
            placeholder="Token"
          />
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <FiltersPopup
          dexPair={pair}
          exchange={EExchange.dex}
          type={EDexPairFilters.walletsTransfer}
        />
        <div className="text-gray-400">Selected: {selectedIds.length}</div>
      </div>

      <div
        className="border border-[#2A2A2A] rounded-lg overflow-hidden"
        onScroll={handleScroll}
        style={{ maxHeight: 320, overflowY: 'auto' }}
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#1E1E1E] text-left">
              <th className="px-3 py-2 w-12">
                <TableCheckbox
                  id="transfer-recipients-select-all"
                  checked={allSelected}
                  indeterminate={isIndeterminate}
                  onChange={handleToggleAll}
                />
              </th>
              <th className="px-3 py-2">Wallet address</th>
              <th className="px-3 py-2 text-right">Balance ({token.symbol})</th>
            </tr>
          </thead>
          <tbody>
            {wallets.map((wallet: IPairWallet) => {
              const balanceValue =
                wallet.tokens?.find(t => t.token.id === token.id)?.balance ?? '0';
              const displayBalance = formatToken(bnFrom(balanceValue), token.decimals);
              const checked = selectedIds.includes(wallet.id);
              const checkboxId = `transfer-recipient-${wallet.id}`;

              return (
                <tr
                  key={wallet.id}
                  className="border-t border-[#2A2A2A] hover:bg-[#1A1A1A] transition-colors"
                >
                  <td className="px-3 py-2">
                    <TableCheckbox
                      id={checkboxId}
                      checked={checked}
                      indeterminate={false}
                      onChange={() => {
                        handleToggle(wallet);
                      }}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <TableAddressChip
                      address={wallet.address}
                      network={pair.network}
                      className="text-gray-200"
                    />
                  </td>
                  <td className="px-3 py-2 text-right text-gray-300">{displayBalance}</td>
                </tr>
              );
            })}
            {(isLoading || isFetchingNextPage) && (
              <tr>
                <td colSpan={3} className="px-3 py-4 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            )}
            {!wallets.length && !isLoading && (
              <tr>
                <td colSpan={3} className="px-3 py-4 text-center text-gray-400">
                  No wallets found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

StepSelectRecipients.displayName = 'StepSelectRecipients';
